import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  CurrencyDollarIcon,
  ShoppingCartIcon,
  CogIcon,
  UsersIcon,
  ShieldCheckIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';

const HealthScoreCard = ({ healthData, onScoreClick, onDomainClick, showDetails = false }) => {
  const [expandedDomain, setExpandedDomain] = useState(null);

  const domains = [
    {
      key: 'finance',
      name: 'Finance',
      icon: CurrencyDollarIcon,
      color: 'blue',
      gradient: 'from-blue-50 to-blue-100',
      borderColor: 'border-blue-200',
      textColor: 'text-blue-700',
      description: 'Financial health, cash flow, profitability',
    },
    {
      key: 'sales',
      name: 'Sales',
      icon: ShoppingCartIcon,
      color: 'green',
      gradient: 'from-green-50 to-green-100',
      borderColor: 'border-green-200',
      textColor: 'text-green-700',
      description: 'Revenue growth, customer acquisition, market share',
    },
    {
      key: 'operations',
      name: 'Operations',
      icon: CogIcon,
      color: 'purple',
      gradient: 'from-purple-50 to-purple-100',
      borderColor: 'border-purple-200',
      textColor: 'text-purple-700',
      description: 'Process efficiency, quality control, scalability',
    },
    {
      key: 'hr',
      name: 'Human Resources',
      icon: UsersIcon,
      color: 'orange',
      gradient: 'from-orange-50 to-orange-100',
      borderColor: 'border-orange-200',
      textColor: 'text-orange-700',
      description: 'Team performance, culture, talent retention',
    },
    {
      key: 'compliance',
      name: 'Compliance',
      icon: ShieldCheckIcon,
      color: 'red',
      gradient: 'from-red-50 to-red-100',
      borderColor: 'border-red-200',
      textColor: 'text-red-700',
      description: 'Legal compliance, risk management, governance',
    },
  ];

  const getScoreColor = score => {
    if (score >= 80) return 'bg-emerald-50 text-emerald-700 border-emerald-200';
    if (score >= 60) return 'bg-amber-50 text-amber-700 border-amber-200';
    if (score >= 40) return 'bg-orange-50 text-orange-700 border-orange-200';
    return 'bg-red-50 text-red-700 border-red-200';
  };

  const getScoreIcon = score => {
    if (score >= 80) return CheckCircleIcon;
    if (score >= 60) return InformationCircleIcon;
    if (score >= 40) return ExclamationTriangleIcon;
    return XCircleIcon;
  };

  const getScoreLabel = score => {
    if (score >= 80) return 'Excellent';
    if (score >= 60) return 'Good';
    if (score >= 40) return 'Fair';
    return 'Poor';
  };

  const getOverallScore = () => {
    const scores = domains.map(domain => healthData[domain.key]?.score || 0);
    return Math.round(scores.reduce((sum, score) => sum + score, 0) / scores.length);
  };

  const overallScore = getOverallScore();
  const OverallScoreIcon = getScoreIcon(overallScore);

  const handleDomainClick = domainKey => {
    if (onDomainClick) {
      onDomainClick(domainKey);
    } else {
      setExpandedDomain(expandedDomain === domainKey ? null : domainKey);
    }
  };

  const handleOverallScoreClick = () => {
    if (onScoreClick) {
      onScoreClick();
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm md:p-6"
    >
      <div className="mb-6 text-center">
        <motion.div
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={handleOverallScoreClick}
          className={`inline-flex cursor-pointer items-center gap-3 rounded-2xl bg-gradient-to-r from-gray-50 to-gray-100 p-4 transition-all duration-200 hover:from-gray-100 hover:to-gray-200 md:p-6 ${
            onScoreClick ? 'cursor-pointer' : 'cursor-default'
          }`}
        >
          <div className="flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-blue-600 text-white">
            <OverallScoreIcon className="h-10 w-10" />
          </div>
          <div className="text-left">
            <h2 className="text-xl font-bold text-gray-900 md:text-2xl">Overall Health Score</h2>
            <div className="text-3xl font-bold text-blue-600 md:text-4xl">{overallScore}%</div>
            <p className="text-xs text-gray-600 md:text-sm">{getScoreLabel(overallScore)}</p>
          </div>
        </motion.div>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        {domains.map(domain => {
          const score = healthData[domain.key]?.score || 0;
          const DomainIcon = domain.icon;
          const ScoreIcon = getScoreIcon(score);

          return (
            <motion.div
              key={domain.key}
              whileHover={{ scale: 1.02, y: -2 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => handleDomainClick(domain.key)}
              className={`cursor-pointer rounded-xl border ${domain.borderColor} bg-gradient-to-br ${domain.gradient} p-4 shadow-sm transition-all duration-200 hover:shadow-md ${
                onDomainClick ? 'cursor-pointer' : 'cursor-default'
              }`}
            >
              <div className="mb-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className={`rounded-lg bg-white p-2 ${domain.textColor}`}>
                    <DomainIcon className="h-5 w-5" />
                  </div>
                  <h3 className="font-semibold text-gray-900">{domain.name}</h3>
                </div>
                <ScoreIcon className={`h-5 w-5 ${getScoreColor(score).split(' ')[1]}`} />
              </div>

              <div className="mb-2">
                <div className="mb-1 flex items-center justify-between text-sm">
                  <span className="text-gray-600">Score</span>
                  <span className={`font-semibold ${domain.textColor}`}>{score}%</span>
                </div>
                <div className="h-2 w-full rounded-full bg-white">
                  <div
                    className={`h-full rounded-full bg-gradient-to-r ${
                      score >= 80
                        ? 'from-emerald-400 to-emerald-500'
                        : score >= 60
                          ? 'from-amber-400 to-amber-500'
                          : score >= 40
                            ? 'from-orange-400 to-orange-500'
                            : 'from-red-400 to-red-500'
                    }`}
                    style={{ width: `${score}%` }}
                  />
                </div>
              </div>

              <p className="text-xs text-gray-600">{domain.description}</p>

              {showDetails && healthData[domain.key]?.details && (
                <motion.div
                  initial={false}
                  animate={{ height: expandedDomain === domain.key ? 'auto' : 0 }}
                  className="mt-3 overflow-hidden"
                >
                  <div className="rounded-lg bg-white/50 p-2 text-xs text-gray-700">
                    {healthData[domain.key].details}
                  </div>
                </motion.div>
              )}
            </motion.div>
          );
        })}
      </div>

      {onScoreClick && (
        <div className="mt-6 flex justify-center">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleOverallScoreClick}
            className="rounded-lg bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-2 font-medium text-white transition-all duration-200 hover:from-blue-700 hover:to-blue-800 hover:shadow-lg"
          >
            View Detailed Assessment
          </motion.button>
        </div>
      )}
    </motion.div>
  );
};

export default HealthScoreCard;
